import { deleteTaskData, getPropertyData, getTasksData, getVendorsData } from '@/apis/apis'
import useDocumentTitle from '@/components/Helmet'
import { useCustomLoader } from '@/components/Loader'
import GlobalAutocomplete from '@/components/material/Autocomplete'
import CustomDialog from '@/components/material/CustomDialog'
import CustomTable from '@/components/material/CustomTable'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Box, Grid, TextField, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'wouter'

type TaskProps = {
    tasks: any[],
    counts: number,
}

const Index = () => {
    useDocumentTitle("Tasks");
    const [location, navigate] = useLocation();
    const loader = useCustomLoader();
    const { toast } = useToast();
    const [tasks, setTasks] = useState<TaskProps>({ tasks: [], counts: 0 });
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [deleteData, setDeleteData] = useState<any>(null);

    const [vendors, setVendors] = useState<any>({});
    const [vendorSearch, setVendorSearch] = useState("");
    const [selectedVendor, setSelectedVendor] = useState<any>(null);
    const [searchProperty, setSearchProperty] = useState("");
    const [property, setProperty] = useState<any>({});
    const [selectedProperty, setSelectedProperty] = useState<any>(null);
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);

    const fetchTask = useCallback(async (search : string) => {
        loader(true);
        try {
            const data = await getTasksData({ page, limit, search, propertyId: selectedProperty?._id, vendor: selectedVendor?._id, startDate, endDate });
            setTasks(data);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    },[page, limit, selectedProperty, selectedVendor, startDate, endDate])

    useEffect(() => {
        const timer = setTimeout(() => {
            loader(true);
            fetchTask(search)
        }, search ? 600 : 0)
        return () => clearTimeout(timer)
    }, [fetchTask,search])

    const deleteTask = async () => {
        try {
            const data = await deleteTaskData(deleteData?._id);
            if (data?.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: data.message,
                })
                setDeleteData(null);
                fetchTask(search);
            }
            else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: data.message,
                })
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
    }

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const data = await getPropertyData({ filters: { active: true, search: searchProperty }, page: 0, rowsPerPage: 10 });
                setProperty(data);
            } catch (error: any) {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: error?.response?.data?.message || "Something went wrong",
                })
            }
        }
        const timer = setTimeout(() => {

            fetchProperty();

        }, 600)
        return () => clearTimeout(timer);
    }, [searchProperty])

    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const data = await getVendorsData({ page: 0, limit: 10, search: vendorSearch, active: true, });
                setVendors(data);
            } catch (error: any) {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: error?.response?.data?.message || "Something went wrong",
                })
            }
        }
        const timer = setTimeout(() => {
            fetchVendors();

        }, 600)
        return () => clearTimeout(timer);
    }, [vendorSearch])
    return (
        <div>
            <Box className='flex justify-between mb-2'>
                <Typography variant="h6">Task</Typography>
                <Button onClick={() => navigate('/task/add')}><Plus/> Add</Button>
            </Box>
            <Box>
                <Grid container spacing={2}>
                    <Grid size={{ sm: 3, xs: 12 }}>
                        <TextField
                            fullWidth
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size='small'
                        />
                    </Grid>
                    <Grid size={{ sm: 3, xs: 12 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                format='MM-DD-YYYY'
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                }}
                                slotProps={{
                                    textField: {
                                        size: "small", // 👈 replaces renderInput with props
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid size={{ sm: 3, xs: 12 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                format='MM-DD-YYYY'
                                onChange={(newValue) => {
                                    setEndDate(newValue);
                                }}
                                slotProps={{
                                    textField: {
                                        size: "small", // 👈 replaces renderInput with props
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid size={{ sm: 3, xs: 12 }}>
                        <GlobalAutocomplete
                            label="Vendor"
                            options={vendors?.vendors}
                            getOptionLabel={(option: any) => option.name}
                            value={selectedVendor}
                            onChange={(newValue) => {
                                setSelectedVendor(newValue);
                            }}
                            onInputChange={(value) => {
                                setVendorSearch(value);
                            }}
                            placeholder="Please Enter Vendor"
                            size='small'
                        />
                    </Grid>
                    <Grid size={{ sm: 3, xs: 12 }}>
                        <GlobalAutocomplete
                            label="Property"
                            options={property?.data}
                            getOptionLabel={(option: any) => option.name}
                            value={selectedProperty}
                            onChange={(newValue) => {
                                setSelectedProperty(newValue);
                            }}
                            onInputChange={(value) => {
                                setSearchProperty(value);
                            }}
                            placeholder="Please Select Property"
                            size='small'
                        />
                    </Grid>
                </Grid>

            </Box>
            <Box>
                <CustomTable
                    headers={[
                        { id: 'title', label: 'Title' },
                        { id: 'scheduledDate', label: 'Schedule Date' },
                        { id: 'type', label: 'Type' },
                        { id: 'propertyId.name', label: 'Property Name' },
                        { id: 'unitId', label: 'Unit Name' },
                        { id: 'vendor.name', label: 'Vendor Name' },
                        { id: 'status', label: 'Status' },
                    ]}
                    data={tasks?.tasks}
                    count={tasks?.counts}
                    page={page}
                    limit={limit}
                    setPage={setPage}
                    setLimit={setLimit}
                    updateStatus={fetchTask}
                    actions={[
                        { label: "View", icon: <Eye size={16} />, onClick: (row) => navigate(`/task/${row._id}`) },
                        { label: "Edit", icon: <Pencil size={16} />, onClick: (row) => navigate(`/task/edit/${row._id}`) },
                        { label: "Delete", icon: <Trash2 size={16} />, onClick: (row) => setDeleteData(row), color: "red" },
                    ]}
                />
            </Box>
            {deleteData && <CustomDialog
                title='Delete Task'
                content={`Are you sure you want to delete <span style="color: red; font-weight: bold">${deleteData?.title}</span> task?`}
                onClose={() => setDeleteData(null)}
                onConfirm={deleteTask}
            />}

        </div>
    )
}

export default Index